import { store } from "src/store/store";
import debug from "src/modules/debug-logger";
import { getActivityValue } from "src/modules/activity-log/activity";
import { getDefaultAvatar } from "src/modules/avatar/duck/selectors";
import { getDevicesToNotify } from "src/modules/scheduler/duck/selectors";
import { Activities } from "src/modules/activity-log/activity";

import {
  createAwakeNotification,
  createMotionNotification,
  createSleepingNotification
} from "./notifications";

const WAIT_FOR_AUDIO_SAMPLE_TIMEOUT = 10 * 1000;
const TIMEOUT_BEFORE_SLEEP = 60 * 1000;
const MOTION_TIMEOUT = 2000;

export default function createPushNotificator({
  monitoringId,
  messenger,
  getActivityLogActivity,
  eventManager
}) {
  const parents = {};

  async function sendAwakeNotification(parentJid, audioUrl) {
    const parent = parents[parentJid];
    if (parent == null) return;

    const imageUrl = await eventManager.createNotificationImageUrl({
      activity: Activities.AWAKE,
      monitoringId
    });

    const selectedAvatar = getDefaultAvatar(store.getState());
    const notification = createAwakeNotification({
      monitoringId,
      audioUrl,
      avatarName: selectedAvatar && selectedAvatar.name,
      avatarSex: selectedAvatar && selectedAvatar.sex,
      imageUrl
    });
    debug.pushNotification(
      `About to send AwakeNotification to parent`,
      parentJid
    );
    messenger.sendPushNotification(parentJid, notification);
  }

  function sendSleepingNotification(parentJid) {
    const parent = parents[parentJid];
    if (parent == null) return;

    const selectedAvatar = getDefaultAvatar(store.getState());
    const notification = createSleepingNotification({
      monitoringId,
      avatarName: selectedAvatar && selectedAvatar.name,
      avatartSex: selectedAvatar && selectedAvatar.sex
    });

    debug.pushNotification(
      `About to send SleepingNotification to parent`,
      parentJid
    );
    messenger.sendPushNotification(parentJid, notification);
  }

  async function sendMotionNotification(parentJid) {
    const parent = parents[parentJid];
    if (parent == null) return;

    const imageUrl = await eventManager.createNotificationImageUrl({
      activity: Activities.MOTION,
      monitoringId
    });

    const selectedAvatar = getDefaultAvatar(store.getState());
    const notification = createMotionNotification({
      monitoringId,
      avatarName: selectedAvatar && selectedAvatar.name,
      avatartSex: selectedAvatar && selectedAvatar.sex,
      imageUrl
    });

    debug.pushNotification(
      `About to send MotionNotification to parent`,
      parentJid
    );
    messenger.sendPushNotification(parentJid, notification);
  }

  function onSleeping() {
    Object.values(parents).forEach((parent) => {
      const { jid, lastSentActivity, activityOnEntrance, awakeTimeoutId } =
        parent;

      if (lastSentActivity === Activities.SLEEPING && awakeTimeoutId == null)
        return;
      if (
        lastSentActivity == null &&
        activityOnEntrance === Activities.SLEEPING &&
        awakeTimeoutId == null
      )
        return;

      parent.sleepingTimeoutId = setTimeout(() => {
        sendSleepingNotification(jid);
        parent.lastSentActivity = Activities.SLEEPING;
        parent.sleepingTimeoutId = null;
      }, TIMEOUT_BEFORE_SLEEP);
    });
  }

  function onAwake() {
    Object.values(parents).forEach((parent) => {
      const {
        jid,
        awakeTimeoutId,
        sleepingTimeoutId,
        motionTimeoutId,
        lastSentActivity,
        activityOnEntrance
      } = parent;
      clearTimeout(motionTimeoutId);
      clearTimeout(sleepingTimeoutId);
      parent.sleepingTimeoutId = null;
      parent.motionTimeoutId = null;

      if (lastSentActivity === Activities.AWAKE) return;
      if (awakeTimeoutId) return;
      if (lastSentActivity == null && activityOnEntrance === Activities.AWAKE)
        return;

      if (
        lastSentActivity == null &&
        getActivityLogActivity() === Activities.AWAKE
      ) {
        sendAwakeNotification(jid);
        parent.lastSentActivity = Activities.AWAKE;
      } else {
        debug.pushNotification(
          `About to setTimeout for Awake notification, for parent`,
          parent
        );
        parent.awakeTimeoutId = setTimeout(() => {
          sendAwakeNotification(jid);
          parent.lastSentActivity = Activities.AWAKE;
          parent.awakeTimeoutId = null;
        }, WAIT_FOR_AUDIO_SAMPLE_TIMEOUT);
      }
    });
  }

  function onMotion() {
    Object.values(parents).forEach((parent) => {
      const {
        jid,
        awakeTimeoutId,
        sleepingTimeoutId,
        motionTimeoutId,
        lastSentActivity,
        activityOnEntrance
      } = parent;
      clearTimeout(sleepingTimeoutId);
      clearTimeout(awakeTimeoutId);
      parent.sleepingTimeoutId = null;
      parent.awakeTimeoutId = null;
      if (lastSentActivity === Activities.MOTION) return;
      if (motionTimeoutId) return;
      if (lastSentActivity == null && activityOnEntrance === Activities.MOTION)
        return;
      if (
        lastSentActivity == null &&
        getActivityLogActivity() === Activities.MOTION
      ) {
        sendMotionNotification(jid);
        parent.lastSentActivity = Activities.MOTION;
      } else {
        debug.pushNotification(
          `About to setTimeout for Motion notification, for parent`,
          parent
        );
        parent.motionTimeoutId = setTimeout(() => {
          sendMotionNotification(jid);
          parent.lastSentActivity = Activities.MOTION;
          parent.motionTimeoutId = null;
        }, MOTION_TIMEOUT);
      }
    });
  }

  return {
    getIfShouldRequestAudioSample() {
      return (
        Object.values(parents).length > 0 &&
        Object.values(parents).some(
          (parent) =>
            parent.lastSentActivity === Activities.SLEEPING ||
            (parent.lastSentActivity == null &&
              parent.activityOnEntrance === Activities.SLEEPING)
        )
      );
    },
    startedScheduledMonitoring() {
      const parentsToNotify = getDevicesToNotify(store.getState());
      Object.values(parentsToNotify).forEach((parent) => {
        parents[parent.jid] = {
          jid: parent.jid,
          lastSentActivity: null,
          activityOnEntrance: Activities.SLEEPING,
          awakeTimeoutId: null,
          sleepingTimeoutId: null,
          motionTimeoutId: null
        };
      });
    },
    stoppedScheduledMonitoring() {
      const parentsToRemove = getDevicesToNotify(store.getState());
      Object.values(parentsToRemove).forEach((parent) => {
        delete parents[parent.jid];
      });
    },
    parentEnteredBackgroundMode(parent, activity) {
      if (parents[parent.jid]) {
        clearTimeout(parent.awakeTimeoutId);
        clearTimeout(parent.sleepingTimeoutId);
        clearTimeout(parent.motionTimeoutId);
      }
      parents[parent.jid] = {
        jid: parent.jid,
        lastSentActivity: null,
        activityOnEntrance: activity,
        awakeTimeoutId: null,
        sleepingTimeoutId: null,
        motionTimeoutId: null
      };
    },
    parentLeftBackgroundMode(jid) {
      const parent = parents[jid];
      if (parent == null) return;
      clearTimeout(parent.awakeTimeoutId);
      clearTimeout(parent.sleepingTimeoutId);
      clearTimeout(parent.motionTimeoutId);
      delete parents[jid];
    },
    onActivityChange(activity) {
      getActivityValue(activity, {
        AWAKE: onAwake,
        SLEEPING: onSleeping,
        MOTION: onMotion
      })();
    },
    onSample(url) {
      debug.pushNotification(`onSample called, url ${url}`);
      Object.values(parents).forEach((parent) => {
        if (parent.awakeTimeoutId) {
          clearTimeout(parent.awakeTimeoutId);
          parent.awakeTimeoutId = null;
          sendAwakeNotification(parent.jid, url);
          parent.lastSentActivity = Activities.AWAKE;
        }
      });
    },
    destroy() {
      Object.values(parents).forEach((parent) => {
        clearTimeout(parent.awakeTimeoutId);
        clearTimeout(parent.sleepingTimeoutId);
        clearTimeout(parent.motionTimeoutId);
      });
    }
  };
}
